import { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Grid,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { equipmentCategoryValues } from "dora-contracts";
import { UFDoraSelect } from "dora-shared/frontend";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  selectInitialFormData,
  selectIsOpen,
} from "../../../ducks/app/equipment-type-dialog/selectors";
import { FormData, close } from "../../../ducks/app/equipment-type-dialog";
import * as dataActions from "../../../ducks/data/equipment-types";
import { loadProducts } from "../../../ducks/data/products";
import { selectProducts } from "../../../ducks/data/products/selectors";
import emptyToNull from "./empty-to-null";

const OpenEquipmentTypeDialog = ({
  initialData,
}: {
  initialData: FormData;
}) => {
  const { t } = useTranslation(["equipment", "types"]);
  const dispatch = useAppDispatch();
  const products = useSelector(selectProducts);
  useEffect(() => {
    dispatch(loadProducts());
  }, [dispatch]);
  const categoryOptions = useMemo(
    () =>
      equipmentCategoryValues.map((c) => ({
        value: c,
        label: t(`equipment:categories.${c}`),
      })),
    [t]
  );
  const productOptions = useMemo(
    () => [
      { value: null, label: t("equipment:types.fields.noProduct") },
      ...products.map((p) => ({
        value: p.id,
        label: `${p.productNumber} – ${p.name}`,
      })),
    ],
    [products, t]
  );
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm<FormData>({ defaultValues: initialData });
  const canBeEmptied = watch("canBeEmptied");

  const onSubmit = async (values: FormData) => {
    const payload = {
      name: values.name,
      category: values.category,
      isNumbered: values.isNumbered,
      canBeEmptied: values.canBeEmptied,
      defaultDailyPrice: emptyToNull(values.defaultDailyPrice),
      defaultWeeklyPrice: emptyToNull(values.defaultWeeklyPrice),
      defaultMonthlyPrice: emptyToNull(values.defaultMonthlyPrice),
      defaultDeliveryFee: emptyToNull(values.defaultDeliveryFee),
      defaultPickupFee: emptyToNull(values.defaultPickupFee),
      defaultEmptyingPrice: emptyToNull(values.defaultEmptyingPrice),
      color: emptyToNull(values.color),
      icon: emptyToNull(values.icon),
      note: emptyToNull(values.note),
      rentalProductId: emptyToNull(values.rentalProductId),
      // Only meaningful for types that can be emptied; cleared when the
      // checkbox is off so a stale product does not linger on the type.
      emptyingProductId: values.canBeEmptied
        ? emptyToNull(values.emptyingProductId)
        : null,
      returnProductId: emptyToNull(values.returnProductId),
    };
    if (initialData.id) {
      await dispatch(
        dataActions.updateEquipmentType({ ...payload, id: initialData.id })
      );
    } else {
      await dispatch(dataActions.addEquipmentType(payload));
    }
  };

  return (
    <Dialog open onClose={() => dispatch(close())} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {initialData.id
            ? t("equipment:types.editTitle")
            : t("equipment:types.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("equipment:types.fields.name")}
              required
              {...register("name", { required: true })}
              fullWidth
            />
            <UFDoraSelect
              name="category"
              control={control}
              label={t("equipment:types.fields.category")}
              options={categoryOptions}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <Controller
                control={control}
                name="isNumbered"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t("equipment:types.fields.isNumbered")}
                  />
                )}
              />
              <Controller
                control={control}
                name="canBeEmptied"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t("equipment:types.fields.canBeEmptied")}
                  />
                )}
              />
            </Stack>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultDailyPrice")}
                  {...register("defaultDailyPrice")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultWeeklyPrice")}
                  {...register("defaultWeeklyPrice")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultMonthlyPrice")}
                  {...register("defaultMonthlyPrice")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultDeliveryFee")}
                  {...register("defaultDeliveryFee")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultPickupFee")}
                  {...register("defaultPickupFee")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  type="number"
                  inputProps={{ step: "0.01", min: 0 }}
                  label={t("equipment:types.fields.defaultEmptyingPrice")}
                  {...register("defaultEmptyingPrice")}
                  fullWidth
                />
              </Grid>
            </Grid>
            <div>
              <UFDoraSelect
                name="rentalProductId"
                control={control}
                label={t("equipment:types.fields.rentalProduct")}
                options={productOptions}
                nullable
                fullWidth
              />
            </div>
            <Grid container spacing={2}>
              <Grid size={6}>
                <UFDoraSelect
                  name="emptyingProductId"
                  control={control}
                  label={t("equipment:types.fields.emptyingProduct")}
                  options={productOptions}
                  nullable
                  disabled={!canBeEmptied}
                  fullWidth
                />
                {!canBeEmptied && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {t("equipment:types.fields.emptyingProductDisabledHelp")}
                  </Typography>
                )}
              </Grid>
              <Grid size={6}>
                <UFDoraSelect
                  name="returnProductId"
                  control={control}
                  label={t("equipment:types.fields.returnProduct")}
                  options={productOptions}
                  nullable
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label={t("equipment:types.fields.note")}
              {...register("note")}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(close())} color="inherit">
            {t("equipment:actions.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t("equipment:actions.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const EquipmentTypeDialog = () => {
  const open = useSelector(selectIsOpen);
  const initialData = useSelector(selectInitialFormData);
  if (!open || !initialData) {
    return null;
  }
  return <OpenEquipmentTypeDialog initialData={initialData} />;
};

export default EquipmentTypeDialog;
